import { StoryblokComponent } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import type { ComponentProps } from "react";
import { Container } from "@/components/ui/Container";
import { Link } from "@/components/ui/Link";
import { Reveal } from "@/components/ui/Reveal";
import { CarouselTrack } from "@/components/ui/sections/_lib/CarouselTrack";
import { Typography } from "@/components/ui/Typography";
import type { ExplorationCategoryStoryblok } from "@/generated/sb/sb-types";
import { Link as NextLink } from "@/i18n/routing";
import { isDarkMode } from "@/lib/utils/isDarkMode";

export interface ExplorationCategoryProps extends ComponentProps<"section"> {
	heading?: string;
	linkLabel?: string;
	linkHref?: string;
	categories?: ExplorationCategoryStoryblok["categories"];
	bg?: string;
	preview: boolean;
}

export const ExplorationCategory = ({
	heading,
	linkLabel,
	linkHref,
	categories,
	className,
	preview,
	bg,
	...rest
}: ExplorationCategoryProps) => {
	const items = categories ?? [];
	const hasHeading = Boolean(heading);
	const hasLink = Boolean(linkLabel && linkHref);
	const darkMode = isDarkMode(bg);

	return (
		<section {...rest} style={{ backgroundColor: bg ? bg : undefined }}>
			<Container size="large">
				<div
					className={clsx(
						"py-grid-section-vertical-lg flex flex-col gap-grid-section-vertical-md",
						className,
					)}
				>
					{(hasHeading || hasLink) && (
						<div className="flex w-full items-end gap-grid-component-spacing-2xs">
							<div className="flex min-w-px flex-1 flex-col gap-grid-component-spacing-xs">
								{heading && (
									<Typography
										as="h2"
										variant="heading-display-xs"
										className={clsx(
											"w-full text-text-dark-text-1",
											darkMode && "text-text-light-text-1",
										)}
									>
										{heading}
									</Typography>
								)}
							</div>
							{hasLink && linkHref && (
								<Link
									as={NextLink}
									href={linkHref}
									variant="primary"
									size="medium"
									className={clsx(darkMode && "text-text-light-text-1")}
								>
									{linkLabel}
								</Link>
							)}
						</div>
					)}
					<CarouselTrack layout="fill">
						{items.map((blok, i) => (
							<Reveal key={blok._uid} animation="fade-bottom" delay={100 * (i + 1)}>
								<StoryblokComponent blok={blok} preview={false} />
							</Reveal>
						))}
					</CarouselTrack>
				</div>
			</Container>
		</section>
	);
};
